import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Button } from '@carbon/react';
import { Add } from '@carbon/react/icons';

import routes from '~/routes';
import type { DataTableSelectionState } from 'components/data-table';
import type { ExportCallback, TableState } from 'components/data-table/types';

import {
  assetsSelectionDisabledAtom,
  awaitingDraftMaterializationAtom,
  draftAssetsQueryLoadableAtom,
  draftReloadAtom,
  draftTablesReloadAtom,
  focusedAssetIdAtom,
  subtractTicksPendingAtom,
  togglePinAtom,
} from '../atoms';
import { usePackageWizardCtx } from '../context';
import { useDraftAssetChange } from '../draft-build-hooks';
import type {
  DraftAssetRow,
  DraftAssetSelectionState,
  DraftAssetsResponse,
  DraftAssetsUpdatePayload,
} from '../types';
import {
  buildAssetSelectionFilters,
  draftAssetHolding,
  draftAssetRowId,
  heldInPartRowIds,
  draftAssetsQueryScope,
  firstTableState,
} from '../utils';
import { AssetsTableView } from './assets-table-view';

/** Props for {@link AssetsTable}. */
export interface AssetsTableProps {
  /**
   * UUID of the draft to read assets from. Universe + as_of_date come
   * from the draft itself (BE-side) — the FE only carries the id.
   */
  draftId: string;
  /**
   * Opens the Add Wellbore modal (INS-9039). When omitted, the button
   * doesn't render — Step 1 only opts in for entity-source packages.
   * Per the v5 mock the button lives next to the "Assets (N selected)"
   * heading on this table, not on the slots toolbar.
   */
  onAddWellbore?: () => void;
  /**
   * Opens the Add LTD modal (INS-9040). Same opt-in pattern as
   * `onAddWellbore`.
   */
  onAddLtd?: () => void;
  /** Show every owning entity of a Tract: the draft came from an asset list. */
  allEntities?: boolean;
  /**
   * Holds selection, and the Create Wellbore / LTD buttons, still while the
   * parent rebuilds the draft (a scenario switch or a queued build), on top of
   * the table's own in-flight gating.
   */
  disableSelection?: boolean;
}

const EMPTY_SET: Set<string> = new Set();
const NO_TICKS: ReadonlyMap<string, boolean> = new Map();

/** How long Step 1 waits after a Subtract tick for more, before sending them as one change. */
export const SUBTRACT_TICK_PAUSE_MS = 600;

// Synthetic id stuffed into `toggledNodes` so DataTable's header renders
// indeterminate when the BE reports `mixed` but no rows on the visible
// page are selected. The sentinel doesn't match any real `salesforceId`,
// so cells render unchecked and the diff handler ignores it.
const MIXED_SENTINEL = '__be_mixed_marker__';

/**
 * Step 1 assets table. Server-paginated off
 * `POST /queries/packages/drafts/:draft_id/assets`. The draft owns its
 * universe + as-of date, so this component carries only the draft id.
 *
 * Selection model is BE-driven (mirrors {@link SlotsTable}): each row
 * carries `selected`, `meta.selectionState` is the aggregate, click →
 * `updateDraftAssets` PATCH → refetch. No client-side selection cache;
 * the BE is the only source of truth. A tick or untick, one row or the
 * header, changes only the selection and waits for nothing; a change that
 * rebuilds a Tract (its Subtracts, or a Tract held in part made whole or
 * unticked) holds the whole step until the build is done.
 *
 * A Tract with more than one Subtract is ticked whole, in part or not at all
 * through its Subtract rows; its own checkbox shows which (checked, partly,
 * unchecked), and a click on a partly-ticked Tract makes it whole. Subtract
 * ticks show at once and are sent together, as one change, after a pause of
 * {@link SUBTRACT_TICK_PAUSE_MS}; the Tract checkboxes hold still meanwhile.
 * They stay shown until the rows they were sent for come back.
 *
 * Rendering is delegated to {@link AssetsTableView} — this wrapper owns
 * the data layer, selection diff, and pin focus; the view owns columns,
 * sub-tract expand, and the DataTable shell.
 *
 * @param {AssetsTableProps} props - draft id
 * @return {JSX.Element} The assets table
 */
export const AssetsTable: FC<AssetsTableProps> = ({
  draftId,
  onAddWellbore,
  onAddLtd,
  allEntities = false,
  disableSelection = false,
}) => {
  const { form, backgroundDownload } = usePackageWizardCtx();
  const focusedAssetId = useAtomValue(focusedAssetIdAtom);
  const togglePin = useSetAtom(togglePinAtom);

  const [queryState, fetchAssets] = useAtom(draftAssetsQueryLoadableAtom);
  const changeAssets = useDraftAssetChange(form);
  const selectionDisabled = useAtomValue(assetsSelectionDisabledAtom);
  const [ticksPending, setTicksPending] = useAtom(subtractTicksPendingAtom);
  // Step 1's wait for a build a change queued: a click now would PATCH against
  // a draft still building.
  const isAwaitingMaterialization = useAtomValue(
    awaitingDraftMaterializationAtom
  );
  const reload = useAtomValue(draftReloadAtom);
  const tablesReload = useAtomValue(draftTablesReloadAtom);

  const [patchError, setPatchError] = useState<string | null>(null);

  // Hold the most recent TableState so refetch-after-mutation can
  // re-issue the same page/search scope.
  const lastParamsRef = useRef<TableState | null>(null);

  // Stale-while-revalidate: hold the last successful response so a
  // post-PATCH refetch keeps the rows visible. DataTable then renders its
  // refetch progress bar over the (dimmed) stale rows instead of unmounting
  // into a skeleton — preserving scroll position. The skeleton only shows on
  // the initial load, when there's no cached response yet. Held in state, set
  // while rendering as React's "information from previous renders" pattern does.
  const [lastResponse, setLastResponse] = useState<DraftAssetsResponse | null>(
    null
  );
  if (queryState.state === 'hasData' && queryState.data !== lastResponse) {
    setLastResponse(queryState.data);
  }
  const cachedResponse =
    queryState.state === 'hasData' ? queryState.data : lastResponse;

  const loadData = useCallback(
    (params: TableState) => {
      lastParamsRef.current = params;
      fetchAssets({
        draftId,
        page: params.page,
        pageSize: params.pageSize,
        ...draftAssetsQueryScope(params),
        // Wizard map merges these row-level geometries with the
        // selected-only `maps/features` set; opt in here so they ride
        // the same fetch.
        includeGeometry: true,
      });
    },
    [draftId, fetchAssets]
  );

  // DataTable doesn't self-fire `loadData` on mount in paginated mode;
  // kick off the initial fetch when the draft id changes. Default sort
  // is by Name ASC — matches the table's user-visible column header
  // order and gives a deterministic order on first paint. Each settled
  // change to the draft (`draftReloadAtom`, `draftTablesReloadAtom`)
  // re-reads the same page, sort and filters, so the rows show what the
  // server holds.
  useEffect(() => {
    loadData(lastParamsRef.current ?? firstTableState('name'));
  }, [draftId, reload, tablesReload, loadData]);

  const data = useMemo<DraftAssetRow[]>(
    () => cachedResponse?.results ?? [],
    [cachedResponse]
  );
  const totalCount = cachedResponse?.totalCount ?? 0;
  const isLoading = queryState.state === 'loading';
  const meta = cachedResponse?.meta ?? null;
  const selectionState: DraftAssetSelectionState =
    meta?.selectionState ?? 'none';
  const selectedAssetCount = meta?.selectedAssetCount ?? 0;

  // Subtract ticks the rows don't show yet, by Subtract id: those waiting out
  // the pause (`unsentRef`), then those sent (`sentRef`) until the rows come
  // back from the refetch the change ends with.
  const [ticks, setTicks] = useState<ReadonlyMap<string, boolean>>(NO_TICKS);
  const unsentRef = useRef(new Map<string, boolean>());
  const sentRef = useRef<Set<string> | null>(null);
  const tickTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  useEffect(() => {
    const sent = sentRef.current;
    if (sent === null) return;
    sentRef.current = null;
    setTicks((prev) => new Map([...prev].filter(([id]) => !sent.has(id))));
  }, [data]);

  // The rows as the user has ticked them. A Tract's held count is the server's, moved by the
  // ticks not yet sent, so its "(N of M Subtracts)" keeps up with its checkboxes.
  const rows = useMemo<DraftAssetRow[]>(
    () =>
      ticks.size === 0
        ? data
        : data.map((row) => {
            if (
              !row.subtracts.some((subtract) =>
                ticks.has(subtract.salesforceId)
              )
            ) {
              return row;
            }
            let moved = 0;
            const subtracts = row.subtracts.map((subtract) => {
              const selected =
                ticks.get(subtract.salesforceId) ?? subtract.selected;
              moved += Number(selected) - Number(subtract.selected);
              return { ...subtract, selected };
            });
            const includedSubtractCount =
              typeof row.includedSubtractCount === 'number'
                ? row.includedSubtractCount + moved
                : row.includedSubtractCount;
            return { ...row, subtracts, includedSubtractCount };
          }),
    [data, ticks]
  );

  // A Tract ticked in part is partly checked and kept out of the selection, so
  // a click on it selects it whole.
  const indeterminateRowIds = useMemo(
    () => new Set(heldInPartRowIds(rows)),
    [rows]
  );

  // Canonical mapping from BE truth → DataTable's `{selectAll, toggledNodes}`,
  // matching SlotTable's pattern. `mixed` with no visible selections falls
  // back to a sentinel id so the header stays indeterminate, as it does while
  // a visible Tract is ticked in part.
  const selection: DataTableSelectionState = useMemo(() => {
    const whole = rows.filter((row) => draftAssetHolding(row) === 'whole');
    if (selectionState === 'all' && whole.length === rows.length) {
      return { selectAll: true, toggledNodes: [] };
    }
    if (whole.length > 0) {
      return { selectAll: false, toggledNodes: whole.map(draftAssetRowId) };
    }
    if (selectionState === 'none' && indeterminateRowIds.size === 0) {
      return { selectAll: false, toggledNodes: [] };
    }
    return { selectAll: false, toggledNodes: [MIXED_SENTINEL] };
  }, [selectionState, rows, indeterminateRowIds]);

  const highlightedRowIds = useMemo(
    () => (focusedAssetId !== null ? new Set([focusedAssetId]) : EMPTY_SET),
    [focusedAssetId]
  );

  const handlePinToggle = useCallback(
    (id: string) => togglePin({ kind: 'asset', id }),
    [togglePin]
  );

  // Sends one asset change. `useDraftAssetChange` waits out any build it
  // queues, holding the step, then has the step re-read the draft; a refused
  // change shows here. `sentTicks` are the Subtract ticks it carries, shown
  // until those rows come back.
  const applyChange = useCallback(
    async (payload: DraftAssetsUpdatePayload, sentTicks?: Set<string>) => {
      try {
        await changeAssets(payload);
        setPatchError(null);
      } catch (err) {
        setPatchError(
          err instanceof Error ? err.message : 'Failed to update draft assets'
        );
      } finally {
        if (sentTicks) sentRef.current = sentTicks;
      }
    },
    [changeAssets]
  );

  const handleRowSelect = useCallback(
    async (next: DataTableSelectionState) => {
      // Header click vs row click: DataTable always clears `toggledNodes`
      // on header click, so any flip in `selectAll` against the current
      // BE-derived selection IS a header click. Header clicks dispatch
      // bulk mode (`{ selectAll, filters? }`); per-row clicks dispatch
      // the existing per-id diff against the visible page. A Tract ticked
      // in part is kept out of the selection, so it reads as unselected on
      // every click: only its own click changes it, sending it whole.
      const isHeaderClick = next.selectAll !== selection.selectAll;

      let payload: DraftAssetsUpdatePayload;
      if (isHeaderClick) {
        const filters = buildAssetSelectionFilters(lastParamsRef.current);
        payload =
          filters !== null
            ? { draftId, selectAll: next.selectAll, filters }
            : { draftId, selectAll: next.selectAll };
      } else {
        const nextToggled = new Set(
          next.toggledNodes.filter((id) => id !== MIXED_SENTINEL)
        );
        const addedAssetIds: string[] = [];
        const removedAssetIds: string[] = [];
        for (const row of rows) {
          const rowId = draftAssetRowId(row);
          const holding = draftAssetHolding(row);
          const intendedSelected = next.selectAll
            ? !nextToggled.has(rowId)
            : nextToggled.has(rowId);
          if (intendedSelected && holding !== 'whole') {
            addedAssetIds.push(rowId);
          } else if (!intendedSelected && holding === 'whole') {
            removedAssetIds.push(rowId);
          }
        }
        if (addedAssetIds.length === 0 && removedAssetIds.length === 0) return;
        payload = {
          draftId,
          addedAssetIds: addedAssetIds.length > 0 ? addedAssetIds : undefined,
          removedAssetIds:
            removedAssetIds.length > 0 ? removedAssetIds : undefined,
        };
      }

      await applyChange(payload);
    },
    [draftId, rows, selection.selectAll, applyChange]
  );

  // Sends the Subtract ticks waiting out the pause, as one change.
  const flushTicks = useCallback(() => {
    clearTimeout(tickTimer.current);
    const unsent = unsentRef.current;
    if (unsent.size === 0) return;
    unsentRef.current = new Map();
    setTicksPending(false);
    const ticked = [...unsent];
    const addedSubtractIds = ticked.filter(([, on]) => on).map(([id]) => id);
    const removedSubtractIds = ticked.filter(([, on]) => !on).map(([id]) => id);
    void applyChange(
      {
        draftId,
        addedSubtractIds:
          addedSubtractIds.length > 0 ? addedSubtractIds : undefined,
        removedSubtractIds:
          removedSubtractIds.length > 0 ? removedSubtractIds : undefined,
      },
      new Set(unsent.keys())
    );
  }, [draftId, applyChange, setTicksPending]);

  // Leaving the step sends the ticks still waiting, rather than losing them.
  const flushRef = useRef(flushTicks);
  useEffect(() => {
    flushRef.current = flushTicks;
  }, [flushTicks]);
  useEffect(() => () => flushRef.current(), []);

  const handleSubtractSelect = useCallback(
    (subtractId: string, selected: boolean) => {
      const shown = data
        .flatMap((row) => row.subtracts)
        .find((subtract) => subtract.salesforceId === subtractId);
      // Ticked back to what the draft holds: nothing to send for it.
      const unchanged = shown?.selected === selected;
      setTicks((prev) => {
        const next = new Map(prev);
        if (unchanged) next.delete(subtractId);
        else next.set(subtractId, selected);
        return next;
      });
      if (unchanged) unsentRef.current.delete(subtractId);
      else unsentRef.current.set(subtractId, selected);

      clearTimeout(tickTimer.current);
      const waiting = unsentRef.current.size > 0;
      setTicksPending(waiting);
      if (waiting) {
        tickTimer.current = setTimeout(
          () => flushRef.current(),
          SUBTRACT_TICK_PAUSE_MS
        );
      }
    },
    [data, setTicksPending]
  );

  // Query error takes precedence over PATCH error — a failed load
  // leaves no table to interact with.
  const errorMessage: string | null =
    queryState.state === 'hasError'
      ? queryState.error instanceof Error
        ? queryState.error.message
        : 'An unexpected error occurred'
      : patchError;

  // The BE builds the CSV in a background download, whatever the draft's size.
  // The wizard runs the export, so it stays busy and its message shows on
  // whichever step the user is on when it ends.
  const { building: buildingExport, runExport } = backgroundDownload;

  // Exports the rows the table shows, in its order.
  const handleExport = useCallback<ExportCallback>(
    (_format, filters) =>
      runExport(
        `queries/packages/drafts/${draftId}/assets/export`,
        draftAssetsQueryScope(filters)
      ),
    [runExport, draftId]
  );

  // INS-9064: Create Wellbore + Create LTD + Create CRM Tract buttons.
  // Wellbore and LTD are gated on opt-in callback props (Step 1 wires them
  // only for entity-source packages); Create CRM Tract always renders as a
  // navigate-away link to the existing CRM Tract Creation Wizard.
  const headerActions = useMemo(
    () => (
      <>
        {onAddWellbore && (
          <Button
            kind="ghost"
            size="sm"
            renderIcon={Add}
            onClick={onAddWellbore}
            disabled={disableSelection}
          >
            Create Wellbore Asset
          </Button>
        )}
        {onAddLtd && (
          <Button
            kind="ghost"
            size="sm"
            renderIcon={Add}
            onClick={onAddLtd}
            disabled={disableSelection}
          >
            Create LTD Asset
          </Button>
        )}
        <Button
          kind="ghost"
          size="sm"
          renderIcon={Add}
          href={routes.crmTractsAndSubtractsPath}
          target="_blank"
        >
          Create CRM Tract
        </Button>
      </>
    ),
    [onAddWellbore, onAddLtd, disableSelection]
  );

  return (
    <AssetsTableView
      data={rows}
      totalCount={totalCount}
      isLoading={isLoading}
      errorMessage={errorMessage}
      headerCount={selectedAssetCount}
      showSelectedSuffix
      loadData={loadData}
      searchPlaceholder="Search by Tract Name or tags"
      focusedAssetId={focusedAssetId}
      onPinToggle={handlePinToggle}
      selectionState={selection}
      onRowSelect={handleRowSelect}
      selectionDisabled={
        disableSelection ||
        selectionDisabled ||
        isAwaitingMaterialization ||
        ticksPending
      }
      highlightedRowIds={highlightedRowIds}
      indeterminateRowIds={indeterminateRowIds}
      onSubtractSelect={handleSubtractSelect}
      subtractSelectionDisabled={
        disableSelection || selectionDisabled || isAwaitingMaterialization
      }
      onExport={handleExport}
      exportDisabledReason={buildingExport ? 'Building export' : undefined}
      allEntities={allEntities}
      headerActions={headerActions}
    />
  );
};
